'use client';

import {
  browserGetFeatureFlagVariant,
  browserGetFeatureFlagVariantAsync,
} from '@plura/shared/posthog';
import type { ExperimentSlot, ExperimentVariant } from '@plura/shared/track';
import { getControlVariant } from '@plura/shared/track';
import { trpc } from '@plura/shared/trpc';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTrack } from './useTrack';

/**
 * sessionStorage key for a slot's assigned variant. We persist the
 * assignment so post-checkout / cross-page conversions can attribute to
 * the right variant even after the React tree re-renders or the user
 * navigates away from the slot.
 *
 * Cleared on logout via the existing `posthog.reset()` flow (sessionStorage
 * is wiped on tab close anyway).
 */
const STORAGE_PREFIX = 'plura.exp.';

/**
 * URL query-param prefix used to force a variant for QA / agent testing.
 * Example: `?exp_landing-hero-headline=variant_b` pins the user to that
 * variant for the rest of the session (override persists in sessionStorage
 * under `STORAGE_PREFIX` so navigating to other pages keeps the assignment).
 *
 * Override only applies if the variant id exists in the slot's candidate
 * set (in-code variants + DB-backed treatments) — typos fall through to
 * PostHog as if the override wasn't there, instead of breaking the slot.
 */
const URL_OVERRIDE_PREFIX = 'exp_';

function storedKey(slotKey: string): string {
  return `${STORAGE_PREFIX}${slotKey}`;
}

function persistAssignment(slotKey: string, variantId: string) {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(storedKey(slotKey), variantId);
  } catch {
    // storage may be disabled (incognito quotas etc.) — silently skip.
  }
}

/**
 * Read the URL-param override for a slot, if any. Returns `null` when
 * the param isn't present, when we're SSR, or when the URL can't be
 * parsed (defensive — should never happen in practice).
 *
 * Caller must still validate the returned id against the candidate set
 * before using it — see resolve effect below.
 */
function readUrlOverride(slotKey: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get(`${URL_OVERRIDE_PREFIX}${slotKey}`);
  } catch {
    return null;
  }
}

/**
 * Read the persisted variant id for a slot. Returns `null` when the
 * user hasn't been assigned (or sessionStorage is unavailable). Useful
 * for firing `EXPERIMENT_CONVERTED` from a post-checkout / post-action
 * surface that doesn't itself render the slot.
 */
export function getStoredExperimentVariant(slotKey: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage.getItem(storedKey(slotKey));
  } catch {
    return null;
  }
}

/**
 * Resolve the PostHog variant for an experiment slot, with a guaranteed
 * fallback to the slot's `control` variant so the UI is always
 * renderable.
 *
 * Variant resolution order:
 *   1. PostHog returns a variant id.
 *   2. We look it up in the slot's in-code `variants[]` AND in the DB
 *      `ExperimentVariant` rows fetched via `experiment.getVariantsForSlot`.
 *      The DB rows let new treatments ship without a code deploy — once
 *      the AB_TEST_PROPOSAL executor inserts a row + creates the PostHog
 *      flag variant, the next user assigned to it sees DB-stored copy.
 *   3. If the id matches neither, fall back to control.
 *
 * Behaviour:
 *
 *   - Returns `{ variantId, variant, isReady }`. `variantId` is the
 *     literal id from the resolved variant (code OR DB); `variant` is
 *     the full variant object with `.copy.headline` etc.
 *   - First render returns the control variant with `isReady=false`,
 *     even if PostHog has the flag cached. This guarantees the same
 *     output server-side and client-side (avoids hydration warnings).
 *   - Once PostHog AND the DB variants resolve, the hook re-renders
 *     with the real variant and `isReady=true`.
 *   - Fires `EXPERIMENT_VIEWED` exactly once per `(slot, variant)` per
 *     mount once `isReady` is true.
 *
 * Pair this with a manual `track('EXPERIMENT_CONVERTED', { slot, variant })`
 * at the conversion call-site (e.g. inside the upgrade-button click
 * handler) so the winner-detection cron can compute conversion rates.
 */
export function useExperimentVariant<TSlot extends ExperimentSlot>(slot: TSlot): {
  variantId: string;
  variant: ExperimentVariant;
  isReady: boolean;
} {
  const control = getControlVariant(slot);

  const [resolvedVariantId, setResolvedVariantId] = useState<string>(control.id);
  const [posthogReady, setPosthogReady] = useState(false);
  const trackedRef = useRef(false);
  const { track } = useTrack();

  // Fetch DB-backed treatment variants for this slot. Cached aggressively
  // so opening the same slot in two components doesn't re-fetch — slots
  // change rarely (only when an AB_TEST_PROPOSAL is approved). 5 min stale
  // time is plenty short for "I just executed a draft, why don't I see
  // the variant" but long enough to be effectively free at render time.
  const variantsQuery = trpc.experiment.getVariantsForSlot.useQuery(
    { slotKey: slot.key },
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    }
  );

  // Build the merged candidate set: in-code variants (control + any
  // treatments shipped via code) PLUS DB-backed treatments. DB rows take
  // precedence on id collision so a hot-fix can override a code variant
  // without a deploy (this is intentional — admins can edit copy on a
  // live test by archiving + re-creating the variant).
  const allVariants = useMemo<ExperimentVariant[]>(() => {
    const dbVariants: ExperimentVariant[] = (variantsQuery.data?.variants ?? []).map((row) => ({
      id: row.variantId,
      label: row.label,
      copy: row.copy,
    }));
    const dbIds = new Set(dbVariants.map((v) => v.id));
    const codeVariants = slot.variants.filter((v) => !dbIds.has(v.id));
    return [...codeVariants, ...dbVariants];
  }, [slot.variants, variantsQuery.data]);

  useEffect(() => {
    let cancelled = false;

    const resolve = async () => {
      const candidateIds = new Set(allVariants.map((v) => v.id));

      // 1. URL-param override wins over everything. Used by QA / agent /
      //    operator to force a specific variant without touching PostHog
      //    or sessionStorage manually. Persists into sessionStorage so
      //    follow-up navigations within the session keep the override.
      const urlOverride = readUrlOverride(slot.key);
      if (urlOverride && candidateIds.has(urlOverride)) {
        if (!cancelled) {
          persistAssignment(slot.key, urlOverride);
          setResolvedVariantId(urlOverride);
          setPosthogReady(true);
        }
        return;
      }

      // 2. sessionStorage holds the assignment from earlier in the
      //    session (either previously persisted by PostHog assignment,
      //    earlier URL override, or manual `setItem` from devtools for
      //    QA). Reading from it as a bootstrap means:
      //      - QA can override via console without going through URL
      //      - Cross-page navigation keeps the same variant assignment
      //        even before PostHog finishes loading
      //      - PostHog reassignment mid-session can't accidentally flip
      //        a user to a different variant (which would corrupt funnel
      //        attribution).
      //    We still validate against the candidate set so a stale
      //    storage value (e.g. a since-archived variant) falls through
      //    to PostHog.
      const stored = (() => {
        if (typeof window === 'undefined') return null;
        try {
          return window.sessionStorage.getItem(storedKey(slot.key));
        } catch {
          return null;
        }
      })();
      if (stored && candidateIds.has(stored)) {
        if (!cancelled) {
          setResolvedVariantId(stored);
          setPosthogReady(true);
        }
        return;
      }

      const sync = browserGetFeatureFlagVariant(slot.key);
      if (sync && candidateIds.has(sync)) {
        if (!cancelled) {
          setResolvedVariantId(sync);
          setPosthogReady(true);
        }
        return;
      }

      const async = await browserGetFeatureFlagVariantAsync(slot.key);
      if (cancelled) return;

      if (async && candidateIds.has(async)) {
        setResolvedVariantId(async);
      }
      setPosthogReady(true);
    };

    void resolve();
    return () => {
      cancelled = true;
    };
    // We re-resolve when the candidate set changes (DB variants arrive)
    // because PostHog might have returned an id earlier that we didn't
    // recognise; once the DB row hydrates, that id is now valid.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slot.key, allVariants.length]);

  // Wait for BOTH PostHog and the variants query before declaring ready,
  // so we don't fire EXPERIMENT_VIEWED with control while a DB variant
  // is still in-flight (would corrupt funnel attribution).
  const isReady = posthogReady && !variantsQuery.isLoading;

  useEffect(() => {
    if (!isReady || trackedRef.current) return;
    trackedRef.current = true;
    persistAssignment(slot.key, resolvedVariantId);
    track('EXPERIMENT_VIEWED', {
      slot: slot.key,
      variant: resolvedVariantId,
      surface: slot.surface,
      primary_metric: slot.primaryMetricEvent,
    });
  }, [isReady, resolvedVariantId, slot.key, slot.surface, slot.primaryMetricEvent, track]);

  // Resolve the actual variant object. Merge with control's copy as a
  // base so partial DB variants (e.g. only `headline` set) still render
  // every field the React component expects.
  const matched = allVariants.find((v) => v.id === resolvedVariantId) ?? control;
  const variant: ExperimentVariant = {
    ...matched,
    copy: { ...(control.copy ?? {}), ...(matched.copy ?? {}) },
  };

  return {
    variantId: variant.id,
    variant,
    isReady,
  };
}
